import { Download, Film, Loader2, Trash2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { usePolling } from "@/hooks/use-async";
import { MotionNoiseFields } from "@/components/motion-noise-fields";
import type { Cell, DatasetStatus, Maze, RecordInput } from "@/lib/api";
import { ApiError, DEFAULT_MOTION_NOISE, adminApi } from "@/lib/api";
import { RouteEditor } from "@/routes/admin/mazes/route-editor";

function bytes(n: number | null | undefined): string {
  if (!n) return "—";
  return n >= 1 << 20 ? `${(n / (1 << 20)).toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
}

/**
 * The exploration dataset: the only imagery a student gets before navigating, since a
 * session has no exploration phase.
 *
 * Draw or auto-plan the routes, record, watch the progress the tool reports, then the
 * student archive is built and linked from the challenge page. The two maps with the driven
 * trajectories are shown here for the admin to eyeball -- `stuck_cells` above zero means a
 * route brushes a wall the controller could not get around -- and are never in the archive.
 */
export function DatasetCard({
  maze,
  adminKey,
  onChanged,
}: {
  maze: Maze;
  adminKey: string;
  onChanged: () => void;
}) {
  const challengeId = maze.id; // the dataset is keyed by maze; the name is historical
  const bundle = maze;
  const [routes, setRoutes] = useState<Cell[][]>([]);
  const [options, setOptions] = useState<Required<Omit<RecordInput, "waypoints" | "routes">>>({
    seed: 42,
    capture_every: 1,
    noisy_motion: true,
    linear_sigma: DEFAULT_MOTION_NOISE.linear_sigma,
    angular_sigma: DEFAULT_MOTION_NOISE.angular_sigma,
    speed_scale_sigma: DEFAULT_MOTION_NOISE.speed_scale_sigma,
    turn_scale_sigma: DEFAULT_MOTION_NOISE.turn_scale_sigma,
    heading_bias_sigma: DEFAULT_MOTION_NOISE.heading_bias_sigma,
    jpeg_quality: 90,
    jpeg_chroma: "444",
  });
  const [busy, setBusy] = useState(false);
  const [downloading, setDownloading] = useState(false);

  /**
   * The archive exactly as a student will receive it. `<a href>` cannot carry the admin
   * header, so the bytes come through fetch and out through an object URL; 100-odd MB in
   * memory is fine for the one person who does this.
   */
  async function download() {
    setDownloading(true);
    try {
      const url = await adminApi.fetchBlob(`/admin/mazes/${maze.id}/dataset/archive`, adminKey);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = `${maze.id}-exploration-data.zip`;
      anchor.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Download failed");
    } finally {
      setDownloading(false);
    }
  }

  const status = usePolling<DatasetStatus>(
    () => adminApi.dataset(maze.id, adminKey),
    (s) => s.state !== "recording",
    1000,
    [maze.id, adminKey],
  );
  const state = status.data?.state ?? "none";

  // Refresh the challenge (exploration_data_url changes) when a recording finishes.
  const previousState = useRef(state);
  useEffect(() => {
    if (previousState.current === "recording" && state !== "recording") onChanged();
    previousState.current = state;
  }, [state, onChanged]);

  async function record() {
    const drawn = routes.filter((r) => r.length > 1);
    if (!drawn.length) {
      toast.error("Draw at least one route, or plan some automatically");
      return;
    }
    setBusy(true);
    try {
      await adminApi.recordDataset(
        challengeId,
        {
          waypoints: { start: bundle.spawn!.cell, target: bundle.target!.cell, trajectories: drawn },
          ...options,
        },
        adminKey,
      );
      status.refetch();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed", { duration: 12000 });
    } finally {
      setBusy(false);
    }
  }

  const [confirmingRemove, setConfirmingRemove] = useState(false);

  async function remove() {
    setBusy(true);
    try {
      await adminApi.deleteDataset(challengeId, adminKey);
      setConfirmingRemove(false);
      status.refetch();
      onChanged();
    } catch (caught) {
      toast.error(caught instanceof ApiError ? caught.message : "Failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Exploration dataset</CardTitle>
        <CardDescription>
          What students of every challenge in this maze download to learn it. Draw the routes a
          robot will drive, then record the frames along them.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <RouteEditor maze={maze} adminKey={adminKey} value={routes} onChange={setRoutes} />

        <div className="grid gap-3 sm:grid-cols-4">
          <Num label="Seed" id="r-seed" value={options.seed} onChange={(v) => setOptions((o) => ({ ...o, seed: v }))} />
          <Num label="Capture every N ticks" id="r-cap" value={options.capture_every} min={1} max={20} onChange={(v) => setOptions((o) => ({ ...o, capture_every: v }))} />
          <Num label="JPEG quality" id="r-q" value={options.jpeg_quality} min={50} max={100} onChange={(v) => setOptions((o) => ({ ...o, jpeg_quality: v }))} />
          <div className="grid gap-1.5">
            <Label className="text-xs">Chroma</Label>
            <div className="flex gap-1">
              {(["444", "420"] as const).map((c) => (
                <Button key={c} size="sm" variant={options.jpeg_chroma === c ? "default" : "outline"} onClick={() => setOptions((o) => ({ ...o, jpeg_chroma: c }))}>
                  4:{c.slice(1)}
                </Button>
              ))}
            </div>
          </div>
        </div>

        <MotionNoiseFields
          id="r-noise"
          value={{
            enabled: options.noisy_motion,
            linear_sigma: options.linear_sigma,
            angular_sigma: options.angular_sigma,
            speed_scale_sigma: options.speed_scale_sigma,
            turn_scale_sigma: options.turn_scale_sigma,
            heading_bias_sigma: options.heading_bias_sigma,
          }}
          onChange={(n) =>
            setOptions((o) => ({
              ...o,
              noisy_motion: n.enabled,
              linear_sigma: n.linear_sigma,
              angular_sigma: n.angular_sigma,
              speed_scale_sigma: n.speed_scale_sigma,
              turn_scale_sigma: n.turn_scale_sigma,
              heading_bias_sigma: n.heading_bias_sigma,
            }))
          }
          description="Drive the routes with the same speed noise the challenge will navigate under, so what students train on matches what they are tested on. Set both the same."
        />

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={record} disabled={busy || state === "recording"}>
            {state === "recording" ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Film className="size-4" aria-hidden />
            )}
            {state === "done" ? "Re-record" : "Record"}
          </Button>
          {state === "done" || state === "failed" ? (
            <Button variant="ghost" onClick={() => setConfirmingRemove(true)} disabled={busy}>
              <Trash2 className="size-4" aria-hidden />
              Remove dataset
            </Button>
          ) : null}
          {state === "done" ? (
            <Button variant="outline" onClick={download} disabled={downloading}>
              {downloading ? (
                <Loader2 className="size-4 animate-spin" aria-hidden />
              ) : (
                <Download className="size-4" aria-hidden />
              )}
              Download student archive · {bytes(status.data?.zip_bytes)}
            </Button>
          ) : null}
        </div>

        {state === "recording" && status.data ? (
          <p className="text-muted-foreground font-mono text-xs">
            {status.data.event === "starting"
              ? "starting the renderer…"
              : status.data.event === "packaging"
                ? `packaging ${status.data.frames ?? 0} frames for students…`
                : `route ${(status.data.index ?? 0) + 1} · ${status.data.cells_done ?? 0}/${status.data.cells_total ?? "?"} cells · ${status.data.frames ?? 0} frames`}
          </p>
        ) : null}
        {state === "failed" ? (
          <p className="text-destructive text-sm">{status.data?.error}</p>
        ) : null}
        {state === "done" && status.data ? (
          <DoneSummary challengeId={challengeId} adminKey={adminKey} status={status.data} />
        ) : null}
      </CardContent>
    {/* Recording takes minutes of GPU time and students may have downloaded the archive;
          any challenge on this maze loses its download link. So it asks. */}
      <AlertDialog
        open={confirmingRemove}
        onOpenChange={(next) => !next && !busy && setConfirmingRemove(false)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove this dataset?</AlertDialogTitle>
            <AlertDialogDescription>
              The recorded frames and the student archive are deleted, and every challenge on
              this maze loses its dataset download until one is recorded again. Recording it
              again takes a while. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Keep it</AlertDialogCancel>
            <AlertDialogAction onClick={remove} disabled={busy}>
              Remove dataset
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function DoneSummary({
  challengeId,
  adminKey,
  status,
}: {
  challengeId: string;
  adminKey: string;
  status: DatasetStatus;
}) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  useEffect(() => {
    const created: string[] = [];
    Promise.all(
      (status.previews ?? []).map(async (name) => {
        const url = await adminApi.fetchBlob(
          `/admin/mazes/${challengeId}/dataset/preview/${name}`,
          adminKey,
        );
        created.push(url);
        return [name, url] as const;
      }),
    )
      .then((pairs) => setUrls(Object.fromEntries(pairs)))
      .catch(() => setUrls({}));
    return () => created.forEach((u) => URL.revokeObjectURL(u));
  }, [challengeId, adminKey, status.previews, status.frames]);

  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-[8rem_1fr] gap-1 rounded-md border p-3 text-xs">
        <dt className="text-muted-foreground">Frames</dt>
        <dd className="font-mono">
          {status.frames} across {status.trajectories?.length} route(s) · {status.elapsed_s?.toFixed(1)} s
        </dd>
        <dt className="text-muted-foreground">Coverage</dt>
        <dd className="font-mono">
          {status.coverage
            ? `${status.coverage.covered} / ${status.coverage.free} · ${Math.round(status.coverage.fraction * 100)}%`
            : "—"}
        </dd>
        <dt className={status.stuck_cells ? "text-destructive" : "text-muted-foreground"}>Stuck cells</dt>
        <dd className={status.stuck_cells ? "text-destructive font-mono" : "font-mono"}>
          {status.stuck_cells ?? 0}
          {status.stuck_cells ? " — a route brushes a wall the controller could not get around" : ""}
        </dd>
        <dt className="text-muted-foreground">Student files</dt>
        <dd className="font-mono">{status.student_files} (frames, actions, target views — no poses, no maps)</dd>
      </dl>
      <div className="grid gap-3 sm:grid-cols-2">
        {(status.previews ?? []).map((name) => (
          <figure key={name} className="space-y-1">
            {urls[name] ? (
              <img src={urls[name]} alt={name} className="w-full rounded-md border" />
            ) : (
              <div className="bg-muted/40 aspect-square rounded-md border" />
            )}
            <figcaption className="text-muted-foreground font-mono text-xs">{name} · admin only</figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

function Num({
  id,
  label,
  value,
  onChange,
  min,
  max,
}: {
  id: string;
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id} className="text-xs">
        {label}
      </Label>
      <Input id={id} type="number" value={value} min={min} max={max} onChange={(e) => onChange(Number(e.target.value))} className="font-mono text-xs" />
    </div>
  );
}
